import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { MapPin, Briefcase, Search, X, Filter } from "lucide-react";

const EXP_LEVELS = ["Fresher", "0-1 Years", "1-3 Years", "3-5 Years", "5+ Years"];
const EMP_TYPES = ["Full-time", "Part-time", "Contract", "Internship"];

export default function JobSearch() {
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [domains, setDomains] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [category, setCategory] = useState(params.get("category") || "All");
  const [q, setQ] = useState(params.get("q") || "");
  const [dept, setDept] = useState(params.get("department") || "");
  const [loc, setLoc] = useState(params.get("location") || "");
  const [domain, setDomain] = useState(params.get("domain") || "");
  const [expCat, setExpCat] = useState(params.get("experience_category") || "");
  const [empType, setEmpType] = useState(params.get("employment_type") || "");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    api.get("/career-domains").then(r => setDomains(r.data || [])).catch(() => {});
    api.get("/departments").then(r => setDepartments(r.data || [])).catch(() => {});
    api.get("/locations").then(r => setLocations(r.data || [])).catch(() => {});
  }, []);

  const fetchJobs = React.useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (dept) p.set("department", dept);
    if (loc) p.set("location", loc);
    if (domain) p.set("domain", domain);
    if (expCat) p.set("experience_category", expCat);
    if (empType) p.set("employment_type", empType);
    api.get(`/jobs?${p.toString()}`).then(r => setJobs(r.data || [])).finally(() => setLoading(false));
  }, [q, dept, loc, domain, expCat, empType]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const clear = () => {
    setQ(""); setDept(""); setLoc(""); setDomain(""); setExpCat(""); setEmpType("");
    setCategory("All"); setParams({});
  };

  const filteredDomains = category === "All" ? domains : domains.filter(d => d.category === category);
  const catDomainSet = new Set(filteredDomains.map(d => d.key));
  let visibleJobs = jobs;
  if (category !== "All") visibleJobs = jobs.filter(j => catDomainSet.has(j.domain));
  if (sort === "newest") visibleJobs = [...visibleJobs].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  else if (sort === "oldest") visibleJobs = [...visibleJobs].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  else if (sort === "title") visibleJobs = [...visibleJobs].sort((a, b) => (a.title || "").localeCompare(b.title || ""));

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />
      <section className="bg-[#0A2540] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display text-4xl md:text-5xl font-bold">Find your next role</h1>
          <p className="text-slate-300 mt-2">Explore opportunities across VS-Solutions — IT and Non-IT roles.</p>

          <Tabs value={category} onValueChange={(v) => { setCategory(v); setDomain(""); }} className="mt-6">
            <TabsList className="bg-slate-900 border border-slate-800" data-testid="jobs-category-tabs">
              <TabsTrigger value="All" data-testid="jobs-cat-all">All</TabsTrigger>
              <TabsTrigger value="IT" data-testid="jobs-cat-it">IT</TabsTrigger>
              <TabsTrigger value="Non-IT" data-testid="jobs-cat-nonit">Non-IT</TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="mt-6 bg-white rounded-xl p-3 flex flex-col md:flex-row gap-3 shadow-lg flex-wrap">
            <div className="flex-1 min-w-[220px] flex items-center gap-2 px-3">
              <Search className="w-4 h-4 text-slate-400" />
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title, skill, keyword..." className="border-0 focus-visible:ring-0 shadow-none" data-testid="jobs-search-input" />
            </div>
            <Select value={domain} onValueChange={setDomain}>
              <SelectTrigger className="w-full md:w-48 text-slate-800" data-testid="jobs-filter-domain">
                <SelectValue placeholder="Career Domain" />
              </SelectTrigger>
              <SelectContent>
                {filteredDomains.map(d => (
                  <SelectItem key={d.key} value={d.key}>
                    {d.key} {typeof d.active_jobs === "number" ? `(${d.active_jobs})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={expCat} onValueChange={setExpCat}>
              <SelectTrigger className="w-full md:w-40 text-slate-800" data-testid="jobs-filter-exp"><SelectValue placeholder="Experience" /></SelectTrigger>
              <SelectContent>{EXP_LEVELS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger className="w-full md:w-44 text-slate-800" data-testid="jobs-filter-dept"><SelectValue placeholder="Department" /></SelectTrigger>
              <SelectContent>{departments.map(d => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={loc} onValueChange={setLoc}>
              <SelectTrigger className="w-full md:w-40 text-slate-800" data-testid="jobs-filter-location"><SelectValue placeholder="Location" /></SelectTrigger>
              <SelectContent>{locations.map(l => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={empType} onValueChange={setEmpType}>
              <SelectTrigger className="w-full md:w-40 text-slate-800" data-testid="jobs-filter-type"><SelectValue placeholder="Employment" /></SelectTrigger>
              <SelectContent>{EMP_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={clear} variant="ghost" className="text-slate-700" data-testid="jobs-clear">
              <X className="w-4 h-4 mr-1" />Clear
            </Button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-slate-500" data-testid="jobs-count">
            {loading ? "Loading..." : `${visibleJobs.length} active role${visibleJobs.length !== 1 ? "s" : ""}`}
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40" data-testid="jobs-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              <SelectItem value="title">A → Z</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {visibleJobs.map(j => (
            <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`job-card-${j.id}`}>
              <Card className="p-6 h-full card-hover border-slate-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary" className="text-xs">{j.department}</Badge>
                  {j.domain && <Badge className="text-xs bg-[#0A2540] text-white hover:bg-[#0A2540]">{j.domain}</Badge>}
                </div>
                <h3 className="font-display font-semibold text-lg mt-3 text-[#0A2540]">{j.title}</h3>
                <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                  <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{j.location}</div>
                  <div className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" />{j.experience_min}–{j.experience_max} yrs · {j.employment_type}</div>
                </div>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {(j.required_skills || []).slice(0, 4).map(s => (
                    <Badge key={s} className="bg-blue-100 text-blue-800 border-none hover:bg-blue-100">{s}</Badge>
                  ))}
                </div>
              </Card>
            </Link>
          ))}
        </div>
        {!loading && visibleJobs.length === 0 && (
          <div className="text-center py-24 text-slate-500" data-testid="jobs-empty">
            <Search className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p>No roles match your search. Try adjusting filters or clearing them.</p>
          </div>
        )}
      </section>
      <PublicFooter />
    </div>
  );
}
